// Shared error model for history/report requests.
//
// Why this exists: every history screen used to null its data on a failed
// request, so an outage rendered as the empty state ("Không có dữ liệu") and
// read as "the vehicle had no activity". A failure now carries its own state.
//
// Backend contract (report_api middlewares/history_tier.go, cms_api follows):
//   503 {success:false, code:503, error_code:'HISTORY_TIER_UNAVAILABLE', message}
//   500 {success:false, code:500, message}

export const HISTORY_TIER_UNAVAILABLE = 'HISTORY_TIER_UNAVAILABLE';

export const HISTORY_ERROR_TEMPORARY_MESSAGE =
  'Dữ liệu lịch sử tạm thời không truy cập được, vui lòng thử lại sau';
export const HISTORY_ERROR_GENERIC_MESSAGE =
  'Không thể tải dữ liệu, vui lòng thử lại sau';
export const HISTORY_ERROR_NETWORK_MESSAGE =
  'Không thể kết nối đến máy chủ hoặc quá thời gian chờ, vui lòng kiểm tra mạng và thử lại';

// Normalises whatever a thunk's catch block receives. axiosInterceptor rejects
// with `error.response` (an axios response: {status, data}) or with a string,
// so the old `err.response?.data` was always undefined and the server message
// never reached the reducers.
export function toRejectPayload(err) {
  const resp = err && typeof err === 'object' ? err.response || err : null;
  if (resp && typeof resp.status === 'number') {
    const body = resp.data && typeof resp.data === 'object' ? resp.data : {};
    return {
      httpFailure: true,
      status: resp.status,
      code: body.code,
      error_code: body.error_code,
      message: typeof body.message === 'string' ? body.message : undefined
    };
  }
  return { httpFailure: true, status: 0 };
}

// Text for a failed Excel export. A request that failed (5xx / network, tagged `httpFailure`
// by toRejectPayload) must not be reported as "no data in range". The backend's own 200-with-JSON
// "no data" body is not tagged, so it keeps its message or the caller's empty text.
export function exportErrorMessage(err, emptyText) {
  if (err && err.httpFailure) return buildHistoryError({ payload: err }).message;
  return (err && err.message) || emptyText;
}

// Builds the serialisable error kept in redux from a thunk's rejected action.
// `arg` is the original thunk argument so the screen can re-dispatch it.
export function buildHistoryError(action) {
  const payload =
    action && action.payload && typeof action.payload === 'object'
      ? action.payload
      : {};
  const status = typeof payload.status === 'number' ? payload.status : 0;
  const errorCode =
    typeof payload.error_code === 'string' ? payload.error_code : null;
  const temporary = errorCode === HISTORY_TIER_UNAVAILABLE || status === 503;

  let message;
  if (status > 0 && typeof payload.message === 'string' && payload.message.trim()) {
    message = payload.message;
  } else if (temporary) {
    message = HISTORY_ERROR_TEMPORARY_MESSAGE;
  } else if (status === 0) {
    message = HISTORY_ERROR_NETWORK_MESSAGE;
  } else {
    message = HISTORY_ERROR_GENERIC_MESSAGE;
  }

  return {
    status,
    errorCode,
    message,
    temporary,
    arg: action && action.meta ? action.meta.arg : undefined
  };
}

// Slice helpers. State keeps `historyErrors: { [thunkName]: error }`.
export function setHistoryError(state, key, action) {
  if (!state.historyErrors) state.historyErrors = {};
  state.historyErrors[key] = buildHistoryError(action);
}

export function clearHistoryError(state, key) {
  if (state.historyErrors) delete state.historyErrors[key];
}

export function clearAllHistoryErrors(state) {
  state.historyErrors = {};
}

// Selector factory: (sliceName, key) -> state => error | null
export const selectHistoryError = (sliceName, key) => state =>
  (state[sliceName] &&
    state[sliceName].historyErrors &&
    state[sliceName].historyErrors[key]) ||
  null;
